import React from 'react';
import {
  Eye, EyeOff, CheckCircle, AlertCircle,
} from 'lucide-react';

/**
 * Change-Password form used in Worker Settings.
 * Parent owns state and submit handler.
 */
export default function ChangePasswordSection({
  passwordForm,
  setPasswordForm,
  showPasswords,
  setShowPasswords,
  saving,
  onSubmit,
}) {
  const passwordsMatch = passwordForm.new_password === passwordForm.confirm_password;

  const toggleShow = (field) => setShowPasswords({ ...showPasswords, [field]: !showPasswords[field] });

  const renderField = (field, label, testId, extraProps = {}) => (
    <div>
      <label htmlFor={`${testId}-input`} className="block text-sm font-medium text-gray-700 mb-2">
        {label}
      </label>
      <div className="relative">
        <input
          id={`${testId}-input`}
          type={showPasswords[field] ? 'text' : 'password'}
          value={passwordForm[`${field}_password`]}
          onChange={(e) => setPasswordForm({
            ...passwordForm,
            [`${field}_password`]: e.target.value,
          })}
          className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent pr-12"
          required
          data-testid={testId}
          {...extraProps}
        />
        <button
          type="button"
          onClick={() => toggleShow(field)}
          className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500"
        >
          {showPasswords[field] ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
        </button>
      </div>
    </div>
  );

  return (
    <form onSubmit={onSubmit} className="space-y-6">
      <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
        <p className="text-sm text-amber-800">
          <AlertCircle className="w-4 h-4 inline mr-2" />
          Choose a strong password that you don&apos;t use elsewhere. Password must be at least 6 characters.
        </p>
      </div>

      {renderField('current', 'Current Password', 'current-password-input', { placeholder: 'Enter current password' })}
      {renderField('new', 'New Password', 'new-password-input', { placeholder: 'Enter new password', minLength: 6 })}

      <div>
        {renderField('confirm', 'Confirm New Password', 'confirm-password-input', { placeholder: 'Confirm new password', minLength: 6 })}
        {passwordForm.new_password && passwordForm.confirm_password && (
          <p className={`text-sm mt-2 ${passwordsMatch ? 'text-green-600' : 'text-red-600'}`}>
            {passwordsMatch ? (
              <><CheckCircle className="w-4 h-4 inline mr-1" /> Passwords match</>
            ) : (
              <><AlertCircle className="w-4 h-4 inline mr-1" /> Passwords do not match</>
            )}
          </p>
        )}
      </div>

      <button
        type="submit"
        disabled={saving || !passwordForm.current_password || !passwordForm.new_password || !passwordsMatch}
        className="w-full py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-xl font-semibold hover:shadow-lg transition disabled:opacity-50"
        data-testid="change-password-btn"
      >
        {saving ? 'Changing Password...' : 'Change Password'}
      </button>
    </form>
  );
}
